import React, { useEffect } from 'react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import EmptyStateIcon from 'src/assets/images/user/img-emptystate.png';
import NoData from 'src/shared/components/no-data/NoData';
import { PrimaryButton } from 'src/shared/components/button/Button';
import Loader from 'src/shared/components/loader/Loader';
import { useAppDispatch } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { Access, Resources, AdminUserType } from 'src/shared/types';
import useAuthService from 'src/shared/hooks/useAuthService';
import usePermissions from 'src/shared/hooks/usePermissions';
import { setCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSlice';
import { useGetUserManagementSuperAdminOverviewQuery } from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import useUserManagementList from 'src/app/admin/views/user-management/hooks/useUserManagementList';
import Show from 'src/shared/components/show/Show';
import OrganisationLozenge from 'src/app/admin/components/organisation-lozenge/OrganisationLozenge';
import { updateGeneralUiState } from './state/userManagementSlice';
import styles from './UserManagement.module.scss';
import UserManagementFilters from './components/user-management-filters/UserManagementFilters';
import UserManagementModals from './components/user-management-modals/UserManagementModals';
import UserManagementListForSuperAdmin from './components/super-admin-user-management-list/SuperAdminUserManagementList';
import UserManagementListForCustomerAdmin from './components/customer-admin-user-management-list-for/CustomerAdminUserManagementList';

const pageText = {
    title: {
        [AdminUserType.ADMIN]: 'Admin User Management',
        [AdminUserType.SUPER_ADMIN]: 'Super Admin User Management',
    },
    subtitle: {
        [AdminUserType.ADMIN]: "Assign sites and permissions for your organisation's users",
        [AdminUserType.SUPER_ADMIN]: 'Add users to this organisation and assign their sites & permissions',
    },
    noDataSubTitle: {
        [AdminUserType.ADMIN]:
            "Please create your first user account to begin assigning sites and permissions for your organisation's users.",
        [AdminUserType.SUPER_ADMIN]: 'Begin by adding the customer admin and/or a site admin for this organisation',
    },
};

const UserManagement = () => {
    const dispatch = useAppDispatch();
    const { currentCustomerId, currentCustomerName } = useCustomer();
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { hasPermission, hasProjectLevelPermissionToAnyProject } = usePermissions();

    const canCreateNewUser =
        hasPermission({
            to: Access.CREATE,
            resource: Resources.USER,
        }) ||
        hasProjectLevelPermissionToAnyProject({
            to: Access.CREATE,
            resource: Resources.USER,
        });

    const { isLoading: isLoadingSuperAdminOverview, currentData: superAdminOverview } = useGetUserManagementSuperAdminOverviewQuery(
        {
            customerId: currentCustomerId,
            pagination: { page: 1, pageSize: 1 },
            userType: null,
        },
        { skip: !isCurrentUserSuperAdmin }
    );

    // Same hook as CustomerAdminUserManagementList — one getCustomerUsers subscription (avoids duplicate probe with pageSize:1).
    const customerUserList = useUserManagementList();

    useEffect(() => {
        if (!isCurrentUserSuperAdmin || !superAdminOverview) {
            return;
        }
        dispatch(updateGeneralUiState({ hasCustomerAdminInUsers: Boolean(superAdminOverview.customerAdmins?.length) }));
    }, [dispatch, superAdminOverview, isCurrentUserSuperAdmin]);

    // reset project id to update site menu
    useEffect(() => {
        dispatch(setCurrentProjectId(null));
    }, []);

    const isFetching = isCurrentUserSuperAdmin ? isLoadingSuperAdminOverview : customerUserList.isLoadingCustomerUsers;
    const hasUserProbe = isCurrentUserSuperAdmin ? Boolean(superAdminOverview) : Boolean(customerUserList.currentDataForCustomerAdmin);
    const showLoadingOverlay = isFetching || !hasUserProbe;

    const users = isCurrentUserSuperAdmin
        ? [...(superAdminOverview?.users || [])].filter(Boolean)
        : customerUserList.sortedUsersForCustomerAdmin.filter(Boolean);

    const sectionType = isCurrentUserSuperAdmin ? AdminUserType.SUPER_ADMIN : AdminUserType.ADMIN;

    const contentWithoutData = (
        <>
            <NoData
                image={EmptyStateIcon}
                text='No user accounts'
                className={styles.emptyStateMessage}
                subText={pageText.noDataSubTitle[sectionType]}
                height='auto'
            />
            {canCreateNewUser && (
                <div className={styles.buttonBar}>
                    <PrimaryButton
                        className={styles.createUserButton}
                        onClick={() => dispatch(updateGeneralUiState({ shouldShowNewUserModal: true }))}
                        data-testid='user-management-open-add-user-modal-empty-state'
                    >
                        Add user <span>+</span>
                    </PrimaryButton>
                    <UserManagementModals />
                </div>
            )}
        </>
    );

    const mainContent = (
        <div className={styles.pageWithOverlay} data-testid='user-management-page'>
            {hasUserProbe ? (
                <div className={styles.layoutRoot}>
                    <Show when={users.length} fallback={<div className={styles.emptyState}>{contentWithoutData}</div>}>
                        <div className={styles.withUsersColumn}>
                            <div className={styles.filtersRow}>
                                <UserManagementFilters />
                            </div>
                            <div className={styles.tableHost}>
                                <Show when={sectionType === AdminUserType.SUPER_ADMIN} fallback={<UserManagementListForCustomerAdmin />}>
                                    <UserManagementListForSuperAdmin />
                                </Show>
                            </div>
                            <UserManagementModals />
                        </div>
                    </Show>
                </div>
            ) : (
                <div className={styles.loadingShell} aria-hidden />
            )}
            {showLoadingOverlay && (
                <div
                    className={styles.loadingOverlay}
                    data-testid='user-management-loading-overlay'
                    aria-busy='true'
                    aria-label='Loading user management'
                >
                    <Loader />
                </div>
            )}
        </div>
    );

    const customerDetails =
        isCurrentUserSuperAdmin && currentCustomerName ? (
            <OrganisationLozenge customerId={currentCustomerId} customerName={currentCustomerName} />
        ) : (
            ''
        );

    return (
        <StandardAppLayout
            classNames={{ mainContent: styles.userManagementMainContent }}
            mainContent={mainContent}
            title={pageText.title[sectionType]}
            subTitle={pageText.subtitle[sectionType]}
            lozenges={customerDetails}
        />
    );
};

export default UserManagement;
